import React, { useEffect, useState } from 'react';
import { useRouteMatch } from 'react-router-dom';
import { isEqual } from 'lodash';
import AppSelectBox from 'components/FormElements/AppSelectBox';
import FundInformationStep from './FundInformationStep';
import CompanyInformationStep from './CompanyInformationStep';
import GridContainer from 'components/GridContainer';
import { Grid } from '@mui/material';
import { INVESTOR_REPORTING_VIEW } from 'adminConstants/routes';
import { useFirm } from 'hooks/data/firm/useFirm';
import { useReportingFundInfo } from 'hooks/data/investorReporting/useReportingFundInfo';
import { useInvestorReportingFormContext } from 'contexts/InvestorReportingFormContext';
import { useReportingInvestee } from 'hooks/data/investorReporting/useReportingInvestee';

const STEPS = {
  FUND_UPDATE: 0,
  COMPANY_UPDATE: 1,
};

type Props = {
  onExitForm: any;
};

const InvestorReportingForm: React.FC<Props> = ({ onExitForm }) => {
  const [activeStep, setActiveStep] = useState(STEPS.FUND_UPDATE);

  const handleNavigation = (step) =>
    setActiveStep((currentStep) => {
      const newActiveStep = currentStep + step;
      return Object.values(STEPS).includes(newActiveStep)
        ? newActiveStep
        : currentStep;
    });

  const formContext = useInvestorReportingFormContext();
  const {
    setFundInformation,
    investorReport,
    firmId,
    setFirmId,
    managerOverview,
    setManagerOverview,
    showFirmDropdown,
    submitDate,
    setCompanyInformation,
    //@ts-expect-error
    processFundInformation,
    allActiveFundIds: contextAllActiveFundIds,
    setAllActiveFundIds,
    selectedFundTypes,
  } = formContext;

  const investorReportId = investorReport?._id;

  const isViewPage = useRouteMatch(INVESTOR_REPORTING_VIEW)?.isExact;
  const { firms } = useFirm({ params: {} });

  const {
    data: closedFundsData,
    isFetching: isFetchingClosedFunds,
    isFetched: isFetchedClosedFunds,
  } = useReportingFundInfo(
    !investorReportId ? firmId : null,
    submitDate ? submitDate.toLabel(undefined, 'dd/MM/yyyy') : null,
    selectedFundTypes,
  );

  const allActiveFundIds = investorReportId
    ? investorReport.fundIds
    : closedFundsData?.closedFunds?.map(({ _id }) => _id) ?? [];

  useEffect(() => {
    if (!isEqual(allActiveFundIds, contextAllActiveFundIds)) {
      setAllActiveFundIds(allActiveFundIds);
    }
  }, [contextAllActiveFundIds, allActiveFundIds]);

  const { data: companyInformationData } = useReportingInvestee({
    firmId,
    submitDate: submitDate ? submitDate.toLabel(undefined, 'dd/MM/yyyy') : null,
    fundIds: allActiveFundIds,
    enabled:
      isFetchedClosedFunds &&
      Boolean(!!firmId && !!submitDate && allActiveFundIds?.length),
  });

  useEffect(() => {
    if (!companyInformationData?.result?.length) return;

    const defaults = {
      additionalMetrics: [],
      attachments: [],
      mediaType: 'VIDEO',
      media: [{}],
      updateSummary: '',
      additionalTitle: '',
      additionalValue: '',
      updatedSharePrice: '',
      copyFromCompanies: [],
    };

    setCompanyInformation((existingCompanyInformation) =>
      companyInformationData.result.map((freshCompanyData) => {
        const existingDataForCompany = existingCompanyInformation.find(
          (existingCompanyData) =>
            existingCompanyData.companyId === freshCompanyData.companyId,
        );
        return {
          ...freshCompanyData,
          ...(existingDataForCompany ? existingDataForCompany : defaults),
          disableSharePriceUpdate: Boolean(
            freshCompanyData.disableSharePriceUpdate,
          ),
        };
      }),
    );
  }, [companyInformationData]);

  useEffect(() => {
    if (!closedFundsData) return;

    const filteredClosedFunds = closedFundsData.closedFunds
      ?.filter((item) => !item.tag.isSyndicate)
      .map((item) => ({
        ...item,
        title: '',
        shortDescription: '',
        provideCommentary: false,
        commentary: '',
        role: '',
        copyFund: true,
      }));
    setFundInformation(filteredClosedFunds);
  }, [closedFundsData]);

  const hasActiveFundsToReportOn =
    investorReport?.fundIds?.length || allActiveFundIds.length;

  const onNextStep = (data) => {
    setManagerOverview({
      ...(managerOverview ?? {}),
      ...data.managerOverview,
    });
    setFundInformation(processFundInformation(data.fundInformation));
    handleNavigation(1);
  };

  return activeStep === STEPS.FUND_UPDATE ? (
    <>
      {showFirmDropdown && (
        <GridContainer>
          <Grid
            size={{
              md: 6,
              xs: 12,
            }}
          >
            <AppSelectBox
              required
              data={firms?.data?.result ?? []}
              disabled={isViewPage}
              valueKey="_id"
              labelKey="firmName"
              label="Firm"
              value={firmId}
              variant="outlined"
              //@ts-expect-error
              onChange={(event) => setFirmId(event.target.value)}
            />
          </Grid>
        </GridContainer>
      )}
      <FundInformationStep
        firm={firms?.data?.result?.find(({ _id }) => _id === firmId)}
        isFetching={isFetchingClosedFunds}
        isViewPage={isViewPage}
        hasActiveFundsToReportOn={hasActiveFundsToReportOn}
        onNextStep={onNextStep}
        onExitForm={onExitForm}
      />
    </>
  ) : (
    <CompanyInformationStep
      handleStepBack={() => handleNavigation(-1)}
      isViewPage={isViewPage}
    />
  );
};

export default InvestorReportingForm;
